// ManifestBrokenBanner — ADR-0005 / RFC-0010 §8 (Task 4.1).
//
// Persistent, non-destructive warning shown at the top of the project reader
// when the manifest is broken. Per ADR-0005 the portal keeps serving the last
// successful sync's content BELOW this banner (the banner warns, it does not
// replace). It renders exclusively off the SERIALIZED project fields RFC-0006
// returns — `manifestBroken` / `manifestError` / `manifestBrokenAt` +
// `lastSyncedSha` (N3) — and never reads a raw DB column.

import { Link } from 'react-router-dom';
import { WarningCircle, ArrowSquareOut } from '@phosphor-icons/react';

interface ManifestBrokenBannerProps {
  /** The repo name, for the "See details" link to /projects/:repo/broken. */
  repo: string;
  /** Onset timestamp (RFC-0003 `manifest_broken_at`, serialized by RFC-0006). */
  manifestBrokenAt: string | null;
  /** The last successful sync's commit sha, shown so readers know the vintage. */
  lastSyncedSha: string | null;
}

/** Format an ISO timestamp for the "broken since" text; em-dash if absent. */
function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Short-form a commit sha (first 7 chars) for display. */
function shortSha(sha: string | null): string | null {
  if (!sha) return null;
  return sha.length > 7 ? sha.slice(0, 7) : sha;
}

export function ManifestBrokenBanner({
  repo,
  manifestBrokenAt,
  lastSyncedSha,
}: ManifestBrokenBannerProps) {
  const since = formatDate(manifestBrokenAt);
  const sha = shortSha(lastSyncedSha);

  return (
    <div
      role="status"
      className="mb-8 flex flex-col gap-3 rounded-xl border border-[rgba(255,149,0,0.3)] bg-warning-bg px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0 text-warning">
          <WarningCircle size={18} weight="fill" />
        </span>
        <div className="text-[13px] leading-relaxed text-text">
          <p className="font-semibold">
            {since ? `Manifest broken since ${since}` : 'Manifest is broken'}
          </p>
          <p className="text-muted">
            Showing content from the last successful sync
            {sha ? (
              <>
                {' '}
                (<span className="font-mono">{sha}</span>)
              </>
            ) : null}
            .
          </p>
        </div>
      </div>
      <Link
        to={`/projects/${encodeURIComponent(repo)}/broken`}
        className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg border border-border bg-surface px-3 py-1.5 text-[12px] font-medium text-text/80 transition-colors hover:text-text sm:self-auto"
      >
        See the error
        <ArrowSquareOut size={13} weight="bold" />
      </Link>
    </div>
  );
}
